<template>
  <div class="b-menu-view" :class="classes.container">
    <b-menu-breadcrumb v-if="showBreadcrumb" :items="breadcrumbItems"></b-menu-breadcrumb>

    

    <div id="b-menu-view__resize-observer" class="b-menu-view__menu-container">
      <b-menu-main-bubble :parent="parent" :class="classes.mainBubble">
        <slot name="mainBubbleContent"></slot>
      </b-menu-main-bubble>

      <b-menu-drop-area v-show="StoreHelper.isDragInProgress()"></b-menu-drop-area>

      <b-menu-item-bubble v-for="(child, index) in orderedChildren" :key="child.id" v-vbm-mounted="{ callback: registerMenuItem, index: index }" v-vbm-destroyed="unregisterMenuItem"
                          class="b-menu-view__menu-item-bubble"
                          :class="child.classes"
                          :item="child"
                          @click="navigateTo">
      </b-menu-item-bubble>

      <b-menu-back-bubble v-if="StoreHelper.showBackNavigationBubble()" @click="navigateBack"></b-menu-back-bubble>
      <div class="top-right">
        <component :is="topRightComponent" :parent="parent"></component>
      </div>

      <b-menu-pagination v-if="StoreHelper.isPaginationEnabled() && !StoreHelper.isEditInProgress()" v-model="paginationPage" :count="children.length">
        <template v-if="$scopedSlots.paginationActions" v-slot:paginationActions="props">
          <slot name="paginationActions" v-bind="props"></slot>
        </template>
      </b-menu-pagination>
    </div>
  </div>
</template>

<script>
import BMenuMainBubble from '../components/b-menu-main-bubble'
import BMenuItemBubble from '../components/b-menu-item-bubble'
import BMenuBackBubble from '../components/b-menu-back-bubble'
import BMenuDropArea from '../components/b-menu-drop-area'
import BMenuPagination from '../components/b-menu-pagination'
import BMenuBreadcrumb from '../components/b-menu-breadcrumb'

import ResizeManager from '../util/ResizeManager'
import StoreHelper from '../store/helper'
import RouterHelper from '../util/RouterHelper'
import PositionManger from '../util/PositionManager'
import PaginationHelper from '../util/PaginationHelper'

import EventBus, { Events } from '../event-bus'
import _ from 'lodash'

export default {
  name: 'b-menu-view',
  components: {
    BMenuMainBubble,
    BMenuItemBubble,
    BMenuBackBubble,
    BMenuDropArea,
    BMenuPagination,
    BMenuBreadcrumb
  },
  props: {
    initialChildren: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      StoreHelper,
      parent: null,
      children: [],
      topRightComponent: StoreHelper.getTopRightComponent(),
      breadcrumbItems: [],
      isHomeView: false,
      viewName: ''
    }
  },
  created () {
    this.viewName = this.$route.path
    this.isHomeView = _.isEqual(this.$route.name, StoreHelper.getInitialRouteName())
  },
  mounted () {
    ResizeManager.observe(this.$el.querySelector('.b-menu-view__menu-container'))
    StoreHelper.registerView(this.viewName, this)
  },
  activated () {
    StoreHelper.setActiveView(this)
    StoreHelper.setPaginationPage(0)
    ResizeManager.observe(this.$el.querySelector('.b-menu-view__menu-container'))
  },
  deactivated () {
    ResizeManager.unobserve(this.$el.querySelector('.b-menu-view__menu-container'))
  },
  methods: {
    registerMenuItem (el, index) {
      StoreHelper.registerDragElement(this.viewName, el, index)
      PositionManger.calculateDragElementPosition(el, StoreHelper.getDragElementIndex(this.viewName, el))
      PaginationHelper.redefineElementsVisibility(StoreHelper.getDragElements(this.viewName))
    },
    unregisterMenuItem (el) {
      StoreHelper.removeDragElement(this.viewName, el)
    },
    navigateBack () {
      StoreHelper.navigateBack()
    },
    navigateTo (item) {
      // TODO: export code snippet into router helper
      const route = { path: this.$route.path + '/' + item.identifier }
      if (RouterHelper.routeExists(this.$router, route)) {
        return RouterHelper.goTo(this.$router, route)
      }

      EventBus.$emit(Events.B_MENU_ITEM_CLICKED, item)
    },
    updateParent (item) {
      this.parent = item
    },
    addMenuItems (items) {
      this.children = [...this.children, ...items]
      this.$nextTick(() => {
        PositionManger.recalculateDragElementPositions(StoreHelper.getDragElements(this.viewName))
      })
    },
    removeMenuItem (identifier) {
      this.children = this.children.filter(child => child.identifier !== identifier)
    },
    replaceMenuItem (item) {
      // TODO: Settings 'id'
      this.children = this.children.map(child => child.id === item.id ? item : child)
    },
    replaceOrAddMenuItem (item) {
      this.hasItem(item) ? this.replaceMenuItem(item) : this.addMenuItems([item])
    },
    hasItem (item) {
      return _.gte(_.findIndex(this.children, { id: item.id }), 0)
    }
  },
  computed: {
    classes () {
      return {
        container: {
          'b-menu-view__menu-item-has-hover': StoreHelper.isHoverInProgress(),
          'b-menu-view__menu-item-has-edit': StoreHelper.isEditInProgress()
        },
        mainBubble: StoreHelper.getMenuMainBubbleDefaultClasses()
      }
    },
    paginationPage: {
      get () { return StoreHelper.getPaginationPage() },
      set (page) { StoreHelper.setPaginationPage(page) }
    },
    orderedChildren () {
      return _.orderBy(this.children, 'order')
    },
    showBreadcrumb () {
      return StoreHelper.isBreadcrumbEnabled()
    }
  },
  watch: {
    initialChildren: {
      handler (children) {
        this.children = _.unionBy(this.children, children, 'id')
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
.b-menu-view {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__menu-container {
    position: relative;
    flex: 1;
  }

  &__menu-item-bubble {
    transition: all 350ms ease 100ms;
    transition-delay: 50ms;
  }

  &__menu-item-has-hover &__menu-item-bubble:not(&__menu-item-bubble--hover),
  &__menu-item-has-edit &__menu-item-bubble:not(&__menu-item-bubble--edit) {
    opacity: 0.5;
  }
}

.top-right {
  position: absolute;
  top: 0;
  right: 0;
}
</style>
